import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../../../assets/css/Buyer/aboutus/about.css';

// Counter Animation Hook
const useCounterAnimation = (endValue, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!shouldStart || hasAnimated) return;

    setHasAnimated(true);
    let startTime = null;
    const startValue = 0;

    // Easing function - easeOutExpo for best effect
    const easeOutExpo = (t) => {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    };

    const animateCounter = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = easeOutExpo(progress);
      
      const currentCount = Math.floor(easedProgress * (endValue - startValue) + startValue);
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animateCounter);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animateCounter);
  }, [endValue, duration, shouldStart, hasAnimated]);

  return count;
};

// Individual Stat Card Component
const StatCard = ({ value, suffix = '', label, description, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const statRef = useRef(null);

  // Extract numeric value from string (e.g., "10K+" -> 10000)
  const getNumericValue = (val) => {
    const numStr = val.toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(numStr);
    
    if (val.includes('K')) return num * 1000;
    return num;
  };

  // Get suffix from original value
  const getSuffix = (val) => {
    if (val.includes('K+')) return 'K+';
    if (val.includes('K+')) return 'K+';
    if (val.includes('+')) return '+';
    if (val.includes('%')) return '%';
    return '';
  };

  const numericValue = getNumericValue(value);
  const displaySuffix = getSuffix(value);
  
  const animatedCount = useCounterAnimation(numericValue, 2000, isVisible);

  // Format number for display
  const formatNumber = (num) => {
    if (value.includes('K')) {
      return (num / 1000).toFixed(0);
    }
    return num;
  };

  // Intersection Observer for scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statRef.current) {
      observer.observe(statRef.current);
    }

    return () => {
      if (statRef.current) {
        observer.unobserve(statRef.current);
      }
    };
  }, [isVisible]);

  return (
    <Col md={3} sm={6} className={`animate-on-scroll delay-${delay}`} ref={statRef}>
      <div className={`stat-item ${isVisible ? 'visible' : ''}`}>
        <h2 className="display-4 fw-bold stat-number">
          {formatNumber(animatedCount)}{displaySuffix}
        </h2>
        <p className="text-muted stat-label">{label}</p>
        <small className="text-muted d-block mt-2 stat-description">{description}</small>
      </div>
    </Col>
  );
};

// Main Stats Section Component
const AboutStats = () => {
  const statsData = [
    {
      value: '10K+',
      label: 'Properties Listed',
      description: 'Listed property reflects value, location, and condition.'
    },
    {
      value: '5K+',
      label: 'Happy Clients Served',
      description: 'Happy clients trust us for seamless experiences.'
    },
    {
      value: '100+',
      label: 'Professional Agents',
      description: 'Professional agents delivering expertise, trust, and results.'
    },
    {
      value: '95%',
      label: 'Satisfaction Rate',
      description: 'High satisfaction rate from happy, loyal clients.'
    }
  ];

  return (
    <section className="ab-section-padding ab-bg-light-cream">
      <Container>
        <Row className="align-items-center mb-5">
          <Col lg={6}>
            <h2 className="section-title fw-bold mb-4 animate-on-scroll">
              Don't just buy real estate — <br />
              experience it.
            </h2>
          </Col>
          <Col lg={6}>
            <p className="text-muted animate-on-scroll">
              Don't just look to buy real estate — discover a smoother, smarter journey with us. Our team brings years of market knowledge, client success stories.
            </p>
          </Col>
        </Row>

        <Row className="text-center g-4 mt-4">
          {statsData.map((stat, index) => (
            <StatCard
              key={index}
              value={stat.value}
              label={stat.label}
              description={stat.description}
              delay={index + 1}
            />
          ))}
        </Row>
      </Container>
    </section>
  );
};

export default AboutStats;